import { makeAutoObservable } from 'mobx'

/**
 * Bridge store that lets any part of the app open the Koda chat panel and
 * (optionally) auto-send a prompt with page context once the socket is ready.
 * ChatWidget observes this store and drains `pendingSend`.
 *
 * Context is per-send. The backend remembers the thread's subject, so nothing here has to
 * stay sticky — a stale subject on the client is how follow-ups ended up about the wrong
 * student.
 */
class ChatWidgetStore {
	isOpen = false
	/** { text, kodaContext } queued to send when the panel is open and connected. */
	pendingSend = null
	/** { pathname, context } the open page has, for the pages whose ids are not in the url. */
	pageContext = null

	constructor() {
		makeAutoObservable(this)
	}

	open() {
		this.isOpen = true
	}

	close() {
		this.isOpen = false
	}

	/** Stamped with stampKodaContext so a page the counselor has left cannot still speak. */
	setPageContext(stamped) {
		this.pageContext = stamped || null
	}

	/** Open the panel and queue a prompt to auto-send with the page's Koda context. */
	openWithPrompt({ text, kodaContext } = {}) {
		const message = String(text || '').trim()
		if (!message) {
			this.open()
			return
		}
		this.pendingSend = { text: message, kodaContext: kodaContext || null }
		this.isOpen = true
	}

	get hasPendingSend() {
		return Boolean(this.pendingSend)
	}

	/** Return and clear the queued prompt (called by ChatWidget once it sends). */
	consumePendingSend() {
		const payload = this.pendingSend
		this.pendingSend = null
		return payload
	}
}

export const chatWidgetStore = new ChatWidgetStore()
